import { apiClient } from '@/modules/shared/services/apiClient';
import { ApiResponse, CreateProgramDto, ProgramDto, UpdateProgramDto, ProgramDashboardDto, UserProgramAssignmentDto, CloneProgramRequestDto, CloneProgramResultDto, ProgramDemoSettingsDto } from '@/modules/shared/types';

/**
 * Helper function to convert date string (YYYY-MM-DD) to ISO 8601 format
 * that the .NET backend can parse correctly
 */
const formatDateForApi = (dateString: string): string => {
  if (!dateString) return dateString;
  // Append time to make it a valid ISO 8601 datetime
  return `${dateString}T00:00:00`;
};

export const programService = {
  /**
   * Create a new program. Program is created in DRAFT status.
   * Program code is immutable after creation.
   */
  async createProgram(dto: CreateProgramDto): Promise<ApiResponse<ProgramDto>> {
    // Format dates properly for the backend
    const payload = {
      ...dto,
      programStartDate: formatDateForApi(dto.programStartDate),
      programEndDate: formatDateForApi(dto.programEndDate),
    };
    const response = await apiClient.post<ApiResponse<ProgramDto>>('/programs', payload);
    return response.data;
  },

  /**
   * Get a program by ID
   */
  async getProgramById(programId: string): Promise<ApiResponse<ProgramDto>> {
    const response = await apiClient.get<ApiResponse<ProgramDto>>(`/programs/${programId}`);
    return response.data;
  },

  /**
   * Get program dashboard with setup progress (Super Admin)
   */
  async getProgramDashboard(programId: string): Promise<ApiResponse<ProgramDashboardDto>> {
    const response = await apiClient.get<ApiResponse<ProgramDashboardDto>>(`/programs/${programId}/dashboard`);
    return response.data;
  },

  /**
   * Get all programs
   */
  async getAllPrograms(): Promise<ApiResponse<ProgramDto[]>> {
    const response = await apiClient.get<ApiResponse<ProgramDto[]>>('/programs');
    return response.data;
  },

  /**
   * Get programs assigned to the current user
   */
  async getMyPrograms(): Promise<ApiResponse<UserProgramAssignmentDto[]>> {
    const response = await apiClient.get<ApiResponse<UserProgramAssignmentDto[]>>('/programs/my-assignments');
    return response.data;
  },

  /**
   * Update a program. Program code cannot be changed.
   */
  async updateProgram(programId: string, dto: UpdateProgramDto): Promise<ApiResponse<ProgramDto>> {
    // Format dates properly for the backend
    const payload = {
      ...dto,
      programStartDate: formatDateForApi(dto.programStartDate),
      programEndDate: formatDateForApi(dto.programEndDate),
    };
    const response = await apiClient.put<ApiResponse<ProgramDto>>(`/programs/${programId}`, payload);
    return response.data;
  },

  /**
   * Activate a program. Only DRAFT programs can be activated.
   */
  async activateProgram(programId: string): Promise<ApiResponse<ProgramDto>> {
    const response = await apiClient.post<ApiResponse<ProgramDto>>(`/programs/${programId}/activate`);
    return response.data;
  },

  /**
   * Toggle program-level demo features (auto-play, narration, scenario
   * switcher, demo reset, what-if simulation). SuperAdmin / ProgramAdmin only.
   */
  async updateDemoSettings(programId: string, dto: ProgramDemoSettingsDto): Promise<ApiResponse<ProgramDto>> {
    const response = await apiClient.patch<ApiResponse<ProgramDto>>(
      `/programs/${programId}/demo-settings`,
      dto,
    );
    return response.data;
  },

  /**
   * Clone (duplicate) a program with all its configuration.
   * Clones roles, menu permissions, forms, workflow, and financial plans.
   */
  async cloneProgram(programId: string, dto: CloneProgramRequestDto): Promise<ApiResponse<CloneProgramResultDto>> {
    const payload = {
      ...dto,
      programStartDate: formatDateForApi(dto.programStartDate),
      programEndDate: formatDateForApi(dto.programEndDate),
    };
    const response = await apiClient.post<ApiResponse<CloneProgramResultDto>>(`/programs/${programId}/clone`, payload);
    return response.data;
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // Program Lifecycle Management (Archive, Restore, Delete) - SuperAdmin Only
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Archive a program. Requires SuperAdmin role and re-authentication.
   * Archived programs can be restored but cannot be actively used.
   */
  async archiveProgram(
    programId: string,
    reason: string,
    reAuthToken?: string
  ): Promise<ApiResponse<ProgramLifecycleResultDto>> {
    const response = await apiClient.post<ApiResponse<ProgramLifecycleResultDto>>(
      `/api/superadmin/program-governance/programs/${programId}/archive`,
      { reason, reAuthToken }
    );
    return response.data;
  },

  /**
   * Restore an archived program. Requires SuperAdmin role and re-authentication.
   * Program will be set to INACTIVE status after restoration.
   */
  async restoreProgram(
    programId: string,
    reAuthToken?: string
  ): Promise<ApiResponse<ProgramLifecycleResultDto>> {
    const response = await apiClient.post<ApiResponse<ProgramLifecycleResultDto>>(
      `/api/superadmin/program-governance/programs/${programId}/unarchive`,
      { reAuthToken }
    );
    return response.data;
  },

  async createProgramDeleteExport(programId: string): Promise<ApiResponse<ProgramDeleteExportDto>> {
    const response = await apiClient.post<ApiResponse<ProgramDeleteExportDto>>(
      `/api/superadmin/program-governance/programs/${programId}/delete/export`
    );
    return response.data;
  },

  async createProgramExport(programId: string, fileFormat: 'JSON' | 'CSV_ZIP'): Promise<ApiResponse<ProgramDeleteExportDto>> {
    const response = await apiClient.post<ApiResponse<ProgramDeleteExportDto>>(
      `/api/superadmin/program-governance/programs/${programId}/exports`,
      { fileFormat }
    );
    return response.data;
  },

  async downloadProgramExportFile(exportFile: ProgramDeleteExportDto): Promise<void> {
    const response = await apiClient.get<Blob>(exportFile.fileUrl, { responseType: 'blob' });
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = exportFile.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  },

  async getProgramDependencySummary(programId: string): Promise<ApiResponse<ProgramDependencySummaryDto>> {
    const response = await apiClient.get<ApiResponse<ProgramDependencySummaryDto>>(
      `/api/superadmin/program-governance/programs/${programId}/dependency-summary`
    );
    return response.data;
  },

  async initiateProgramDelete(
    programId: string,
    reason: string,
    options?: { skipExport?: boolean; skipExportConfirmed?: boolean }
  ): Promise<ApiResponse<InitiateDeleteResultDto>> {
    const response = await apiClient.post<ApiResponse<InitiateDeleteResultDto>>(
      `/api/superadmin/program-governance/programs/${programId}/hard-delete/request`,
      { reason, skipExport: options?.skipExport ?? false, skipExportConfirmed: options?.skipExportConfirmed ?? false }
    );
    return response.data;
  },

  async resendProgramDeleteOtp(requestId: string): Promise<ApiResponse<InitiateDeleteResultDto>> {
    const response = await apiClient.post<ApiResponse<InitiateDeleteResultDto>>(
      '/api/superadmin/program-governance/delete/resend-otp',
      { requestId }
    );
    return response.data;
  },

  async verifyProgramDeleteOtp(programId: string, requestId: string, otpCode: string): Promise<ApiResponse<null>> {
    const response = await apiClient.post<ApiResponse<null>>(
      `/api/superadmin/program-governance/programs/${programId}/hard-delete/verify-otp`,
      { requestId, otpCode }
    );
    return response.data;
  },

  async executeProgramDelete(programId: string, requestId: string): Promise<ApiResponse<DeleteExecutionResultDto>> {
    const response = await apiClient.post<ApiResponse<DeleteExecutionResultDto>>(
      `/api/superadmin/program-governance/programs/${programId}/hard-delete/execute`,
      { requestId }
    );
    return response.data;
  },

  async scheduleProgramDelete(programId: string, requestId: string): Promise<ApiResponse<ScheduledDeleteResultDto>> {
    const response = await apiClient.post<ApiResponse<ScheduledDeleteResultDto>>(
      `/api/superadmin/program-governance/programs/${programId}/hard-delete/schedule`,
      { requestId }
    );
    return response.data;
  },

  async cancelProgramDelete(programId: string): Promise<ApiResponse<null>> {
    const response = await apiClient.post<ApiResponse<null>>(
      `/api/superadmin/program-governance/programs/${programId}/hard-delete/cancel`
    );
    return response.data;
  },

  async getProgramAuditTimeline(programId: string): Promise<ApiResponse<ProgramLifecycleTimelineItemDto[]>> {
    const response = await apiClient.get<ApiResponse<ProgramLifecycleTimelineItemDto[]>>(
      `/api/superadmin/program-governance/programs/${programId}/audit-timeline`
    );
    return response.data;
  },

  /**
   * Delete a program (soft hard-delete). Requires SuperAdmin role and re-authentication.
   * Program MUST be archived before deletion. This action is irreversible.
   * Related entities will be cascade soft-deleted.
   */
  async deleteProgram(
    programId: string,
    reason: string,
    confirmText: string,
    reAuthToken: string
  ): Promise<ApiResponse<ProgramLifecycleResultDto>> {
    const response = await apiClient.post<ApiResponse<ProgramLifecycleResultDto>>(
      `/programs/${programId}/delete`,
      { reason, confirmText, reAuthToken }
    );
    return response.data;
  },

  /**
   * Get the impact preview of deleting a program (cascade info)
   */
  async getDeleteImpact(programId: string): Promise<ApiResponse<ProgramDeleteImpactDto>> {
    const response = await apiClient.get<ApiResponse<ProgramDeleteImpactDto>>(
      `/programs/${programId}/delete-impact`
    );
    return response.data;
  },

  /**
   * Export program data as CSV. Uses the analytics export service for the given type.
   */
  async exportProgramData(
    programId: string,
    exportType: 'PROGRAM_KPI' | 'CASES' | 'CLAIMS' | 'DISBURSEMENTS' | 'DOCUMENTS'
  ): Promise<ApiResponse<ProgramExportResultDto>> {
    const response = await apiClient.post<ApiResponse<ProgramExportResultDto>>(
      `/programs/${programId}/analytics/export`,
      { programId, exportType }
    );
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// TypeScript Interfaces for Program Lifecycle
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Result of archive/restore/delete operation
 */
export interface ProgramLifecycleResultDto {
  programId: string;
  programName: string;
  programCode: string;
  action: 'ARCHIVE' | 'RESTORE' | 'DELETE';
  actionTimestamp: string;
  reason: string | null;
  isArchived: boolean;
  isDeleted: boolean;
}

/**
 * Impact preview of program deletion
 */
export interface ProgramDeleteImpactDto {
  programId: string;
  programName: string;
  programCode: string;
  formsCount: number;
  workflowDefinitionsCount: number;
  userAssignmentsCount: number;
  benefitPlansCount: number;
  rolesCount: number;
  totalAffectedEntities: number;
  warnings: string[];
}

export interface InitiateDeleteResultDto {
  requestId: string;
  otpSentTo: string;
  expiresInSeconds: number;
  resendAvailableInSeconds: number;
}

export interface ProgramDeleteExportDto {
  exportFileId: string;
  requestId: string;
  fileName: string;
  fileUrl: string;
  fileSizeBytes: number;
  createdAtUtc: string;
  expiresAtUtc: string;
}

export interface ScheduledDeleteResultDto {
  requestId: string;
  programId: string;
  pendingDeleteAtUtc: string;
  scheduledDeleteAtUtc: string;
  cooldownHours: number;
}

export interface ProgramLifecycleTimelineItemDto {
  auditLogId: string;
  programId: string;
  action: string;
  performedByUserId: string;
  timestampUtc: string;
  reason?: string | null;
  correlationId: string;
  details?: string | null;
}

export interface ProgramDependencySummaryDto {
  patients: number;
  hospitals: number;
  hcps: number;
  assistanceCases: number;
  cases?: number;
  claims: number;
  forms: number;
  workflowSteps: number;
  submissions: number;
  auditLogs: number;
  documents: number;
  ledgerEntries: number;
  roles: number;
  userAssignments: number;
  totalRecords: number;
  totalEntities: number;
}

export interface DeleteExecutionResultDto {
  success: boolean;
  affectedCounts: Record<string, number>;
  cooldownUntilUtc: string | null;
  cooldownDays: number;
  errorMessage?: string;
}

/**
 * Result of program data export
 */
export interface ProgramExportResultDto {
  jobId: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  fileUrl?: string;
  fileName?: string;
  fileSizeBytes?: number;
  recordCount?: number;
  errorMessage?: string;
}
